import { useCallback } from 'react';
import moment from 'moment';

export const DATE_QUERY_FROM_KEY = 'from';
export const DATE_QUERY_DAYS_KEY = 'days';

export interface DateFilterUrlState {
  startDate: string;
  endDate: string;
  count: string;
}

interface ApplyDateFilterArgs {
  startDate?: string | null;
  endDate?: string | null;
  count?: string | null;
}

export const parseDateFiltersFromSearchParams = (
  searchParams: URLSearchParams
): DateFilterUrlState | null => {
  const fromParam = searchParams.get(DATE_QUERY_FROM_KEY);
  const daysParam = searchParams.get(DATE_QUERY_DAYS_KEY);
  const parsedDays = Number(daysParam);

  if (!fromParam || !Number.isInteger(parsedDays) || parsedDays < 1) {
    return null;
  }

  const fromMoment = moment(fromParam, 'YYYY-MM-DD', true);
  if (!fromMoment.isValid()) {
    return null;
  }

  const endMoment = fromMoment.clone().add(parsedDays - 1, 'days');

  return {
    startDate: `${fromMoment.format('YYYY-MM-DD')}T00:00:00.000`,
    endDate: `${endMoment.format('YYYY-MM-DD')}T23:59:59.000`,
    count: parsedDays.toString(),
  };
};

export const removeLegacyDateFilterQueryKeys = (searchParams: URLSearchParams): void => {
  searchParams.delete('startDate');
  searchParams.delete('endDate');
  searchParams.delete('count');
};

export const applyDateFiltersToSearchParams = (
  searchParams: URLSearchParams,
  { startDate, endDate, count }: ApplyDateFilterArgs
): URLSearchParams => {
  const nextParams = new URLSearchParams(searchParams);

  if (startDate && endDate) {
    const from = moment(startDate).format('YYYY-MM-DD');
    const days = count || (moment(endDate).diff(moment(startDate), 'days') + 1).toString();

    nextParams.set(DATE_QUERY_FROM_KEY, from);
    nextParams.set(DATE_QUERY_DAYS_KEY, days);
  } else {
    nextParams.delete(DATE_QUERY_FROM_KEY);
    nextParams.delete(DATE_QUERY_DAYS_KEY);
  }

  removeLegacyDateFilterQueryKeys(nextParams);

  return nextParams;
};

export const useDateFilterUrlParams = () => {
  const parse = useCallback(
    (searchParams: URLSearchParams) => parseDateFiltersFromSearchParams(searchParams),
    []
  );

  const apply = useCallback(
    (searchParams: URLSearchParams, args: ApplyDateFilterArgs) =>
      applyDateFiltersToSearchParams(searchParams, args),
    []
  );

  return {
    parseDateFiltersFromSearchParams: parse,
    applyDateFiltersToSearchParams: apply,
  };
};

